import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Check, ShieldCheck, Minus, Plus, ArrowLeft, Star, Sun, Moon } from "lucide-react";
import { getPromo, setPromo, clearPromo, subscribePromo } from "./promo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Stars = ({ rating }) => (
  <div className="flex gap-1 mb-4" aria-label={`${rating} out of 5 stars`}>
    {[1, 2, 3, 4, 5].map((s) => (
      <Star
        key={s}
        size={14}
        className={s <= rating ? "text-volt fill-volt" : "text-white/20"}
        strokeWidth={1.5}
      />
    ))}
  </div>
);

const GALLERY = [
  { src: "/pack-front.png", alt: "Breethe N°001 nasal strips — 30 pack", label: "The pack" },
  { src: "/buy-duo.png", alt: "Breethe pack — front and back", label: "Front + back" },
  { src: "/buy-exploded.png", alt: "Breethe strip — exploded three-layer view", label: "3 layers" },
  { src: "/buy-strip.png", alt: "Breethe nasal strip close-up", label: "The strip" },
];

const Buy = () => {
  const [loading, setLoading] = useState(null);
  const [promoCode, setPromoCode] = useState("");
  const [promo, setPromoState] = useState(getPromo());
  const [applying, setApplying] = useState(false);
  const [firstPackEmail, setFirstPackEmail] = useState("");
  const [packQty, setPackQty] = useState(1);
  const [shipCfg, setShipCfg] = useState(null);
  const [galleryIdx, setGalleryIdx] = useState(0);
  const [reviews, setReviews] = useState([]);
  const [theme, setTheme] = useState(() => localStorage.getItem("breethe-theme") || "dark");

  useEffect(() => {
    localStorage.setItem("breethe-theme", theme);
  }, [theme]);

  useEffect(() => {
    axios.get(`${API}/reviews`)
      .then(({ data }) => setReviews(data.reviews || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    axios.get(`${API}/shipping/settings`)
      .then(({ data }) => setShipCfg(data))
      .catch(() => {});
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("ref");
    if (ref) {
      setPromoCode(ref.toUpperCase());
      applyCode(ref);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => subscribePromo(setPromoState), []);

  // Active promo: percent codes reprice on the page, fixed codes note the saving
  const pct = promo?.percent_off || 0;
  const money = (v) => v.toFixed(2);
  const firstNow = 14.95 * (1 - pct / 100);
  const subNow = 23.95 * (1 - pct / 100);

  const applyCode = async (raw) => {
    const c = (raw ?? promoCode).trim().toUpperCase();
    if (!c || applying) return;
    setApplying(true);
    try {
      const { data } = await axios.get(`${API}/promo/validate`, { params: { code: c } });
      if (data.valid) {
        setPromo({ code: c, percent_off: data.percent_off, amount_off: data.amount_off, kind: data.kind });
        toast.success(
          `Code ${c} active — ${data.percent_off ? `${data.percent_off}% off` : `$${data.amount_off} off`} your order.`
        );
      } else {
        clearPromo();
        toast.error(data.reason || "That code didn't work.");
      }
    } catch {
      toast.error("Couldn't check that code — try again.");
    } finally {
      setApplying(false);
    }
  };

  const buy = async (lookup, emailOverride = null, qty = 1) => {
    setLoading(lookup);
    let inIframe = false;
    try {
      inIframe = window.self !== window.top;
    } catch {
      inIframe = true;
    }
    const tab = inIframe ? window.open("", "_blank") : null;
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, {
        lookup_key: lookup,
        quantity: qty,
        origin_url: window.location.origin,
        discount_code: promoCode.trim(),
        buyer_email: (emailOverride ?? "").trim(),
      });
      if (tab) {
        tab.location.href = data.checkout_url;
      } else {
        window.location.href = data.checkout_url;
      }
    } catch (e) {
      if (tab) tab.close();
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Checkout failed — try again.");
      setLoading(null);
    }
  };

  // First pack $14.95 intro, additional packs at the normal $27.95
  // with multi-pack tiers applied to the extras (3+ packs −10%, 5+ −15%)
  const tierPct = packQty >= 5 ? 15 : packQty >= 3 ? 10 : 0;
  const extraUnit = Math.round(2795 * (100 - tierPct) / 100) / 100;
  const multiTotal = 14.95 + extraUnit * (packQty - 1);
  const multiNow = multiTotal * (1 - pct / 100);

  const inputCls =
    "w-full h-12 bg-transparent border border-white/20 focus:border-volt px-4 font-plex text-xs uppercase tracking-[0.2em] text-white placeholder:text-white/30 outline-none transition-colors duration-300";

  return (
    <div data-testid="buy-page" data-theme={theme} className="min-h-screen bg-[#0A0A0A] text-white font-manrope flex flex-col">
      <header className="px-6 md:px-10 py-6 border-b border-white/10 flex items-center justify-between">
        <Link to="/" data-testid="buy-logo">
          <img src="/breethe-logo.webp" alt="Breethe" className="brand-logo h-8 w-auto" />
        </Link>
        <div className="flex items-center gap-4">
          <button
            onClick={() => setTheme((t) => (t === "dark" ? "light" : "dark"))}
            data-testid="buy-theme-toggle"
            aria-label="Toggle light theme"
            className="w-11 h-11 border border-white/20 flex items-center justify-center text-white/70 transition-[color,border-color,transform] duration-300 hover:text-volt hover:border-volt active:scale-95"
          >
            {theme === "dark" ? <Sun size={17} /> : <Moon size={17} />}
          </button>
          <Link
            to="/"
            data-testid="buy-back-home"
            className="flex items-center gap-2 font-plex text-[11px] uppercase tracking-[0.25em] text-white/60 hover:text-volt transition-colors duration-300"
          >
            <ArrowLeft size={14} /> Back to site
          </Link>
        </div>
      </header>

      <main className="flex-1 px-6 md:px-10 py-12 md:py-20">
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          {/* Product */}
          <div>
            <p className="font-plex text-xs uppercase tracking-[0.3em] text-volt mb-4">
              Breethe N°001 — Performance nasal strips
            </p>
            <h1 className="font-anton uppercase text-4xl md:text-6xl leading-[0.9]">
              Air in.<br />Doubt out<span className="text-volt">.</span>
            </h1>
            <div data-testid="buy-gallery" className="mt-10">
              <div className="relative border border-white/10 bg-[#121212] buy-stage aspect-square flex items-center justify-center overflow-hidden">
                <img
                  key={GALLERY[galleryIdx].src}
                  src={GALLERY[galleryIdx].src}
                  alt={GALLERY[galleryIdx].alt}
                  data-testid="buy-gallery-main"
                  className="w-4/5 h-4/5 object-contain drop-shadow-[0_20px_40px_rgba(0,0,0,0.25)]"
                />
                <span className="absolute top-4 left-4 font-plex text-[10px] uppercase tracking-[0.3em] text-black/50">
                  {GALLERY[galleryIdx].label}
                </span>
              </div>
              <div className="mt-3 grid grid-cols-4 gap-3">
                {GALLERY.map((g, i) => (
                  <button
                    key={g.src}
                    onClick={() => setGalleryIdx(i)}
                    data-testid={`buy-gallery-thumb-${i}`}
                    aria-label={`Show ${g.label}`}
                    className={`aspect-square bg-[#121212] buy-stage border flex items-center justify-center p-2 transition-[border-color,transform] duration-200 active:scale-95 ${
                      galleryIdx === i ? "border-volt" : "border-white/15 hover:border-white/40"
                    }`}
                  >
                    <img src={g.src} alt="" className="max-w-full max-h-full object-contain" />
                  </button>
                ))}
              </div>
            </div>
            <ul className="mt-8 space-y-3">
              {[
                "Opens your nose by 38% — more oxygen, instantly",
                "Holds 12 hours through training, sweat and sleep",
                "Matte-black, PFAS-free, disappears on your face",
              ].map((b) => (
                <li key={b} className="flex items-start gap-3 text-white/70 font-light text-base">
                  <Check size={18} className="text-volt mt-0.5 shrink-0" />
                  {b}
                </li>
              ))}
            </ul>
            <div data-testid="buy-certifications" className="mt-6 flex flex-wrap gap-3">
              {["TGA Certified", "REACH Certified", "PFAS-Free Materials"].map((c) => (
                <span
                  key={c}
                  className="flex items-center gap-2 border border-white/15 px-3 py-2 font-plex text-[10px] uppercase tracking-[0.2em] text-white/60"
                >
                  <ShieldCheck size={13} className="text-volt" />
                  {c}
                </span>
              ))}
            </div>
          </div>

          {/* Purchase options */}
          <div>
            <p className="font-plex text-xs uppercase tracking-[0.3em] text-white/40 mb-6">
              Choose your way in
            </p>
            <div className="space-y-6">
              {/* First pack — introductory offer */}
              <div data-testid="buy-option-first-pack" className="relative border border-volt bg-volt/5 p-6 md:p-8">
                <span className="absolute -top-px right-0 bg-volt text-black font-plex text-[10px] uppercase tracking-[0.25em] px-4 py-2">
                  Recommended offer
                </span>
                <p className="font-plex text-[10px] uppercase tracking-[0.25em] text-white/50">
                  Breethe · 30 nasal strips
                </p>
                <p className="mt-2 font-anton uppercase text-2xl md:text-3xl">Your First Pack, For Less.</p>
                <div className="mt-4 flex items-baseline gap-3 flex-wrap">
                  <span className="font-anton text-5xl text-volt">${money(pct ? firstNow : 14.95)}</span>
                  <span className="font-anton text-2xl text-white/30 line-through">
                    ${money(pct ? 14.95 : 27.95)}
                  </span>
                  <span className="font-plex text-[10px] uppercase tracking-[0.2em] text-volt border border-volt/60 px-3 py-1.5">
                    {pct ? `Code −${pct}%` : "Introductory price"}
                  </span>
                </div>
                <p className="mt-4 text-white/60 font-light text-sm">
                  30 premium nasal strips. One simple habit.
                </p>
                <ul className="mt-4 space-y-2">
                  {["One-time purchase", "No subscription required", "30 strips included"].map((b) => (
                    <li key={b} className="flex items-center gap-3 text-white/70 font-light text-sm">
                      <Check size={15} className="text-volt shrink-0" />
                      {b}
                    </li>
                  ))}
                </ul>
                <div data-testid="buy-first-pack-qty-box" className="mt-5 border border-white/10 p-4">
                  <div className="flex items-center justify-between">
                    <span className="font-plex text-[10px] uppercase tracking-[0.25em] text-white/50">
                      Quantity
                    </span>
                    <div className="flex items-center border border-white/20">
                      <button
                        onClick={() => setPackQty((q) => Math.max(1, q - 1))}
                        disabled={packQty <= 1}
                        data-testid="buy-first-pack-qty-minus"
                        aria-label="Decrease quantity"
                        className="h-9 w-9 font-anton text-lg text-white/70 transition-colors duration-200 hover:bg-white hover:text-black disabled:opacity-30"
                      >
                        −
                      </button>
                      <span data-testid="buy-first-pack-qty-value" className="w-10 text-center font-anton text-lg leading-none">
                        {packQty}
                      </span>
                      <button
                        onClick={() => setPackQty((q) => Math.min(20, q + 1))}
                        disabled={packQty >= 20}
                        data-testid="buy-first-pack-qty-plus"
                        aria-label="Increase quantity"
                        className="h-9 w-9 font-anton text-lg text-white/70 transition-colors duration-200 hover:bg-white hover:text-black disabled:opacity-30"
                      >
                        +
                      </button>
                    </div>
                  </div>
                  <p className="mt-3 font-plex text-[10px] uppercase tracking-[0.2em] text-volt/80">
                    {packQty === 1
                      ? "First pack at intro price — one per customer"
                      : `First pack $14.95 · extras $${extraUnit.toFixed(2)} each${tierPct > 0 ? ` (${tierPct}% off)` : ""} · 3+ −10% · 5+ −15%`}
                  </p>
                  {packQty > 1 && (
                    <p data-testid="buy-first-pack-qty-total" className="mt-2 font-plex text-xs text-white/70">
                      {packQty} packs = ${multiTotal.toFixed(2)}
                      {" — "}save{" "}
                      <span className="text-volt">${(27.95 * packQty - multiTotal).toFixed(2)}</span>
                      {promo?.percent_off ? (
                        <span className="text-volt"> · with code ${money(multiNow)}</span>
                      ) : null}
                    </p>
                  )}
                </div>
                <input
                  type="email"
                  value={firstPackEmail}
                  onChange={(e) => setFirstPackEmail(e.target.value)}
                  placeholder="YOUR EMAIL — ONE INTRO PACK PER CUSTOMER"
                  data-testid="buy-first-pack-email-input"
                  className={`mt-4 ${inputCls}`}
                />
                <button
                  onClick={() => buy("n001_trial", firstPackEmail, packQty)}
                  disabled={loading !== null}
                  data-testid="buy-first-pack-button"
                  className="mt-4 w-full h-14 bg-volt text-black font-anton uppercase tracking-wider text-base transition-[background-color,transform] duration-200 hover:bg-volt-dark active:scale-[0.98] disabled:opacity-60"
                >
                  {loading === "n001_trial" || loading === "n001_pack"
                    ? "Opening checkout…"
                    : packQty === 1
                      ? `Get your first pack — $${money(pct ? firstNow : 14.95)}`
                      : `Get ${packQty} packs — $${money(pct ? multiNow : multiTotal)}`}
                </button>
                <p className="mt-3 font-plex text-[10px] uppercase tracking-[0.2em] text-white/40">
                  {packQty === 1
                    ? "One per customer · postage calculated at checkout"
                    : "Postage calculated at checkout"}
                </p>
              </div>

              {/* Subscription */}
              <div data-testid="buy-option-subscription" className="border border-white/10 bg-[#121212] p-6 md:p-8">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="font-anton uppercase text-2xl">The Subscription</p>
                    <p className="mt-2 text-white/60 font-light text-sm">
                      Save 14% every month. Free postage, never run dry. Cancel anytime.
                    </p>
                  </div>
                  <p className="font-anton text-3xl whitespace-nowrap">
                    ${money(pct ? subNow : 23.95)}<span className="text-base text-white/50">/mo</span>
                  </p>
                </div>
                {promo?.percent_off ? (
                  <p data-testid="buy-subscription-promo-price" className="mt-2 font-plex text-[10px] uppercase tracking-[0.2em] text-volt">
                    With code {promo.code} — was $23.95/mo
                  </p>
                ) : null}
                <button
                  onClick={() => buy("n001_monthly")}
                  disabled={loading !== null}
                  data-testid="buy-subscription-button"
                  className="mt-5 w-full h-14 bg-white text-black font-anton uppercase tracking-wider text-base transition-[background-color,transform] duration-200 hover:bg-volt active:scale-[0.98] disabled:opacity-60"
                >
                  {loading === "n001_monthly" ? "Opening checkout…" : `Subscribe — $${money(pct ? subNow : 23.95)}/mo`}
                </button>
              </div>
            </div>

            {/* Promo + postage */}
            <div className="pt-2">
              <div className="flex">
                <input
                  type="text"
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                  onKeyDown={(e) => e.key === "Enter" && applyCode()}
                  placeholder="PROMO / REFERRAL CODE (OPTIONAL)"
                  data-testid="buy-promo-input"
                  className={`flex-1 min-w-0 ${inputCls}`}
                />
                <button
                  onClick={() => applyCode()}
                  disabled={applying || !promoCode.trim()}
                  data-testid="buy-promo-apply"
                  className="h-12 px-6 bg-volt text-black font-anton uppercase tracking-wider text-sm shrink-0 transition-[background-color,transform] duration-200 hover:bg-volt-dark active:scale-95 disabled:opacity-50"
                >
                  {applying ? "…" : "Apply"}
                </button>
              </div>
              {promo && (
                <p data-testid="buy-promo-active" className="mt-2 font-plex text-[10px] uppercase tracking-[0.2em] text-volt">
                  Code {promo.code} active —{" "}
                  {promo.percent_off ? `${promo.percent_off}% off — prices updated above` : `$${promo.amount_off} off your order`}
                </p>
              )}
              <p data-testid="buy-shipping-note" className="mt-3 font-plex text-[10px] uppercase tracking-[0.2em] text-white/40">
                {shipCfg?.free_all ? (
                  <>Postage: <span className="text-volt">free on every order</span></>
                ) : (
                  <>
                    Postage:{" "}
                    <span className="text-white/70">${((shipCfg?.fee_cents ?? 799) / 100).toFixed(2)} flat</span> —{" "}
                    <span className="text-volt">
                      free over ${((shipCfg?.free_threshold_cents ?? 5000) / 100).toFixed(2).replace(/\.00$/, "")}
                    </span>{" "}
                    after codes · subscriptions always ship free
                  </>
                )}
              </p>
            </div>
          </div>
        </div>

        {reviews.length > 0 && (
          <div data-testid="buy-reviews" className="max-w-[1400px] mx-auto mt-16 md:mt-24 border-t border-white/10 pt-12">
            <p className="font-plex text-xs uppercase tracking-[0.3em] text-volt mb-4">Field reports</p>
            <h2 className="font-anton uppercase text-3xl md:text-5xl leading-[0.9]">
              Worn loud<span className="text-volt">.</span>
            </h2>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
              {reviews.slice(0, 3).map((r, i) => (
                <div key={i} data-testid={`buy-review-${i}`} className="border border-white/10 bg-[#121212] p-6 md:p-8">
                  <Stars rating={r.rating} />
                  <p className="text-white/70 font-light text-base leading-relaxed">“{r.text}”</p>
                  <p className="mt-5 font-plex text-[10px] uppercase tracking-[0.25em] text-white/40">
                    {r.name} <span className="text-volt">· Verified buyer</span>
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>

      <footer className="px-6 md:px-10 py-6 border-t border-white/10 flex flex-col md:flex-row justify-between gap-3 font-plex text-[10px] uppercase tracking-[0.25em] text-white/30">
        <span>© 2026 Breethe Labs</span>
        <a href="mailto:info@breethe.com.au" className="hover:text-volt transition-colors duration-300">
          info@breethe.com.au
        </a>
      </footer>
    </div>
  );
};

export default Buy;
